import { z } from 'zod'
import { receivedChequeInputSchema } from './cheques.js'
import { moneySchema, optionalText, pageQuerySchema, quantitySchema, versionSchema } from './common.js'
import { omanPlaceRules } from './org-units.js'
import {
  type PaymentMethod,
  type PaymentMethodRef,
  paymentMethodIdSchema,
  paymentMethodSchema,
} from './sales.js'
import type { AssetRef, PlaceStep } from './service-assets.js'
import type { ContractCoverageReason, JobContract, VisitChecklist } from './service-contracts.js'

/**
 * Service jobs and team visits (ADR-022, docs/refactor/SERVICE.md): a customer's request, the visits a
 * team makes, the scope and price the customer agreed, the work done item by item, the invoices raised
 * through Sales and the money technicians report for the accountant to verify.
 */

// ── vocabularies ─────────────────────────────────────────────────────────────

export const SERVICE_PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'URGENT'] as const
export const servicePrioritySchema = z.enum(SERVICE_PRIORITIES)
export type ServicePriority = z.infer<typeof servicePrioritySchema>

/** Stored on the job and recomputed by every command (SERVICE.md §6.1). */
export const SERVICE_JOB_STATUSES = [
  'NEW',
  'SCHEDULED',
  'IN_PROGRESS',
  'PARTIALLY_COMPLETED',
  'COMPLETED',
  'CANCELLED',
] as const
export const serviceJobStatusSchema = z.enum(SERVICE_JOB_STATUSES)
export type ServiceJobStatus = z.infer<typeof serviceJobStatusSchema>

export const SERVICE_VISIT_STATUSES = [
  'SCHEDULED',
  'ON_SITE',
  'IN_PROGRESS',
  'FINISHED',
  'CANCELLED',
] as const
export const serviceVisitStatusSchema = z.enum(SERVICE_VISIT_STATUSES)
export type ServiceVisitStatus = z.infer<typeof serviceVisitStatusSchema>
/** A visit the team has attended: its date, team and times never change again. */
export const SERVICE_ATTENDED_STATUSES: readonly ServiceVisitStatus[] = ['ON_SITE', 'IN_PROGRESS', 'FINISHED']

export const SERVICE_VISIT_KINDS = ['INITIAL', 'FOLLOW_UP'] as const
export type ServiceVisitKind = (typeof SERVICE_VISIT_KINDS)[number]

export const SERVICE_VISIT_OUTCOMES = ['FULL', 'PARTIAL', 'NONE'] as const
export const serviceVisitOutcomeSchema = z.enum(SERVICE_VISIT_OUTCOMES)
export type ServiceVisitOutcome = z.infer<typeof serviceVisitOutcomeSchema>

export const SERVICE_FOLLOW_UP_STATUSES = ['PENDING', 'APPROVED', 'RETURNED', 'WITHDRAWN'] as const
export const serviceFollowUpStatusSchema = z.enum(SERVICE_FOLLOW_UP_STATUSES)
export type ServiceFollowUpStatus = z.infer<typeof serviceFollowUpStatusSchema>

export const SERVICE_FOLLOW_UP_REASONS = [
  'PARTS_NEEDED',
  'MORE_TIME',
  'CUSTOMER_UNAVAILABLE',
  'ACCESS_PROBLEM',
  'OTHER',
] as const
export const serviceFollowUpReasonSchema = z.enum(SERVICE_FOLLOW_UP_REASONS)
export type ServiceFollowUpReason = z.infer<typeof serviceFollowUpReasonSchema>

export const SERVICE_AGREEMENT_STATUSES = ['AWAITING', 'ACCEPTED', 'DECLINED', 'SUPERSEDED'] as const
export type ServiceAgreementStatus = (typeof SERVICE_AGREEMENT_STATUSES)[number]

/** How the customer agreed (or declined) the price. */
export const SERVICE_AGREEMENT_METHODS = [
  'ON_SITE_SIGNED',
  'ON_SITE_VERBAL',
  'PHONE',
  'MESSAGE',
  'EMAIL',
] as const
export const serviceAgreementMethodSchema = z.enum(SERVICE_AGREEMENT_METHODS)
export type ServiceAgreementMethod = z.infer<typeof serviceAgreementMethodSchema>

/**
 * The job's price approval, derived from its latest agreement and its items. CHANGED: accepted, but an
 * item was added or changed since and needs a fresh agreement.
 */
export const SERVICE_PRICE_APPROVALS = [
  'NOT_REQUESTED',
  'AWAITING',
  'ACCEPTED',
  'CHANGED',
  'DECLINED',
] as const
export type ServicePriceApproval = (typeof SERVICE_PRICE_APPROVALS)[number]

/** Derived from the items and the job's non-void invoices (SERVICE.md §5). */
export const SERVICE_BILLING_STATUSES = [
  'NOT_READY',
  'READY',
  'PARTIALLY_INVOICED',
  'FULLY_INVOICED',
  'NO_VISIT_INVOICE',
] as const
export const serviceBillingStatusSchema = z.enum(SERVICE_BILLING_STATUSES)
export type ServiceBillingStatus = z.infer<typeof serviceBillingStatusSchema>

/** Derived from the job's posted invoices and their allocations (Sales); null when nothing is invoiced. */
export const SERVICE_PAYMENT_STATUSES = ['UNPAID', 'PARTIALLY_PAID', 'PAID'] as const
export const servicePaymentStatusSchema = z.enum(SERVICE_PAYMENT_STATUSES)
export type ServicePaymentStatus = z.infer<typeof servicePaymentStatusSchema>

/** CONTRACT: covered by a service contract; visits produce reports, not invoices (extras aside). */
export const SERVICE_BILLING_MODES = ['CHARGEABLE', 'CONTRACT'] as const
export const serviceBillingModeSchema = z.enum(SERVICE_BILLING_MODES)
export type ServiceBillingMode = z.infer<typeof serviceBillingModeSchema>

/**
 * C3: where a job came from. REQUEST: the office or the customer asked (a call-out on a contract). PLANNED:
 * a contract plan's generator made it (never set through the API).
 */
export const SERVICE_JOB_ORIGINS = ['REQUEST', 'PLANNED'] as const
export const serviceJobOriginSchema = z.enum(SERVICE_JOB_ORIGINS)
export type ServiceJobOrigin = z.infer<typeof serviceJobOriginSchema>

export const SERVICE_WAITING = ['PARTS', 'CUSTOMER'] as const
export const serviceWaitingSchema = z.enum(SERVICE_WAITING)
export type ServiceWaiting = z.infer<typeof serviceWaitingSchema>

/** The customer's preferred part of the day; null is any time. */
export const SERVICE_DAY_PARTS = ['MORNING', 'AFTERNOON', 'EVENING'] as const
export const serviceDayPartSchema = z.enum(SERVICE_DAY_PARTS)
export type ServiceDayPart = z.infer<typeof serviceDayPartSchema>

export const SERVICE_COLLECTION_STATUSES = ['REPORTED', 'VERIFIED', 'REJECTED'] as const
export const serviceCollectionStatusSchema = z.enum(SERVICE_COLLECTION_STATUSES)
export type ServiceCollectionStatus = z.infer<typeof serviceCollectionStatusSchema>

/** Problem codes the service API answers with (besides the common ones). */
export const SERVICE_ERROR_CODES = [
  'JOB_CANCELLED',
  'JOB_COMPLETED',
  'JOB_HAS_OPEN_VISIT',
  'JOB_HAS_INVOICES',
  'JOB_HAS_UNBILLED_WORK',
  'SITE_HAS_VISITS',
  'VISIT_OVERLAP',
  'VISIT_ATTENDED',
  'VISIT_NOT_ATTENDED',
  'VISIT_NOT_IN_PROGRESS',
  'VISIT_FINISHED',
  'VISIT_CANCELLED',
  'NOT_TEAM_MEMBER',
  'LEAD_NOT_IN_TEAM',
  'TEAM_ARCHIVED',
  'PRICE_NOT_AGREED',
  'SCOPE_CHANGED',
  'NOTHING_TO_AGREE',
  'AGREEMENT_DECIDED',
  'ITEM_INVOICED',
  'ITEM_BELOW_DONE',
  'QUANTITY_EXCEEDS_REMAINING',
  'NOTHING_TO_INVOICE',
  'NOT_CHARGEABLE',
  'FOLLOW_UP_PENDING',
  'FOLLOW_UP_REQUIRED',
  'VISIT_HAS_WORK',
  'FOLLOW_UP_DECIDED',
  'REPORT_INCOMPLETE',
  'INVOICE_NOT_OF_JOB',
  'INVOICE_NOT_POSTED',
  'COLLECTION_EXCEEDS_BALANCE',
  'COLLECTION_DECIDED',
  'RECEIPT_MISMATCH',
  'RECEIPT_NOT_ALLOCATED',
  'SALES_MODULE_OFF',
  'SITE_NOT_OF_CUSTOMER',
] as const
export type ServiceErrorCode = (typeof SERVICE_ERROR_CODES)[number]

// ── settings ─────────────────────────────────────────────────────────────────

export const serviceSettingsSchema = z.object({
  /** REQUIRED: a chargeable item's work is recorded only once the customer agreed its price. */
  priceApproval: z.enum(['REQUIRED', 'OPTIONAL']),
  /** REQUIRED: a visit finishes only with at least one photo attached to it. */
  reportPhotos: z.enum(['OPTIONAL', 'REQUIRED']),
  /** REQUIRED: a visit finishes only with the name of the customer who acknowledged it. */
  acknowledgment: z.enum(['OPTIONAL', 'REQUIRED']),
  /** C1: sites hold places and assets (Sites & assets screens, jobs pick the assets they're about). */
  assetRegister: z.boolean().default(true),
  /**
   * C3: technicians check each of the job's assets on the visit (checklist, readings, outcome); a planned job
   * finishes FULL only when every asset is checked. Off: no checklist, planned jobs finish as any job.
   */
  assetChecklists: z.boolean().default(true),
  /**
   * Simple mode (2026-10-25): a job whose chargeable total (VAT included) is at or under this amount needs
   * no price approval before work (a call-out fee, a filter). Null: every chargeable item is agreed first.
   */
  priceApprovalThreshold: moneySchema.nullable(),
  /**
   * REQUIRED: a visit is booked with a named lead. OPTIONAL: a visit may be booked for the team only; the
   * member who starts it becomes its lead.
   */
  visitLead: z.enum(['REQUIRED', 'OPTIONAL']),
  /**
   * REQUIRED: a follow-up request waits for a service manager, who books the visit. NOT_NEEDED: the request
   * books the follow-up visit at once (same team and lead, the proposed date or the next working day).
   */
  followUpApproval: z.enum(['REQUIRED', 'NOT_NEEDED']),
})
export type ServiceSettings = z.output<typeof serviceSettingsSchema>

// ── shared pieces ────────────────────────────────────────────────────────────

const timeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM (24-hour)')
const nullableTime = timeSchema.nullish().transform((v) => v ?? null)
const nullableDate = z.iso
  .date()
  .nullish()
  .transform((v) => v ?? null)
const nullableUuid = z
  .uuid()
  .nullish()
  .transform((v) => v ?? null)
const requiredText = (max: number, message = 'Required') => z.string().trim().min(1, message).max(max)
/** A client-made id that makes a retried or double-tapped command return the first result. */
export const requestIdSchema = z.uuid()

const windowOk = (v: { startTime: string | null; endTime: string | null }) =>
  v.startTime === null || v.endTime === null || v.endTime > v.startTime
const windowRule = { path: ['endTime'], message: 'The end is before the start' }

// ── service locations ────────────────────────────────────────────────────────

const httpsUrl = z
  .string()
  .trim()
  .max(500)
  .refine((v) => /^https:\/\/\S+$/i.test(v), 'Use a link starting with https://')
  .nullish()
  .transform((v) => (v ? v : null))

const coordinate = z
  .string()
  .trim()
  .regex(/^-?\d{1,3}(\.\d{1,6})?$/, 'Invalid coordinate')
  .nullish()
  .transform((v) => (v ? v : null))

const siteFieldsBase = z.object({
  /** A short label: "Villa 12, Al Khuwair", "Head office". */
  name: requiredText(120),
  address: requiredText(500),
  area: optionalText(120),
  directions: optionalText(1000),
  mapUrl: httpsUrl,
  contactName: optionalText(120),
  contactPhone: optionalText(40),
  notes: optionalText(1000),
  /** C1: a short code unique in the company ("NOOR-TWR"), used by imports and labels. */
  code: z
    .string()
    .trim()
    .max(20)
    .regex(/^[A-Za-z0-9_.-]+$/, 'Letters, digits, dot, dash and underscore')
    .nullish()
    .transform((v) => (v ? v : null)),
  /** C1: the site template naming its levels (Floor › Apartment); null: places are plain "places". */
  templateId: nullableUuid,
  governorate: optionalText(10),
  wilayat: optionalText(40),
  latitude: coordinate,
  longitude: coordinate,
})
const siteFields = siteFieldsBase.superRefine(omanPlaceRules)
export const serviceSiteFieldsSchema = siteFields
export type ServiceSiteFields = z.input<typeof siteFields>
export const serviceSiteInputSchema = siteFieldsBase
  .extend({ customerId: z.uuid() })
  .superRefine(omanPlaceRules)
export type ServiceSiteInput = z.input<typeof serviceSiteInputSchema>
export const serviceSiteUpdateSchema = siteFieldsBase
  .extend({ version: versionSchema })
  .superRefine(omanPlaceRules)
export type ServiceSiteUpdate = z.input<typeof serviceSiteUpdateSchema>
export const serviceSiteListQuerySchema = z.object({
  customerId: z.uuid(),
  archived: z.stringbool().optional(),
})

/** The sites register's sort fields; "-" first sorts descending. */
export const SERVICE_SITE_SORT_FIELDS = ['name', 'code', 'customer'] as const
export type ServiceSiteSortField = (typeof SERVICE_SITE_SORT_FIELDS)[number]
export const SERVICE_SITE_SORTS = [
  'name',
  '-name',
  'code',
  '-code',
  'customer',
  '-customer',
] as const satisfies readonly (ServiceSiteSortField | `-${ServiceSiteSortField}`)[]
export type ServiceSiteSort = (typeof SERVICE_SITE_SORTS)[number]
/**
 * `GET /service/site-register`: every customer's sites, paged; `q` matches the site's name, code, address
 * and area and its customer's name and code. `archived` lists the archived sites instead.
 */
export const serviceSiteRegisterQuerySchema = pageQuerySchema.extend({
  archived: z.stringbool().optional(),
  sort: z.enum(SERVICE_SITE_SORTS).default('name'),
})
export type ServiceSiteRegisterQuery = z.input<typeof serviceSiteRegisterQuerySchema>

export interface ServiceSite {
  id: string
  customerId: string
  name: string
  address: string
  area: string | null
  directions: string | null
  mapUrl: string | null
  contactName: string | null
  contactPhone: string | null
  notes: string | null
  /** C1 (optional for older fixtures). */
  code?: string | null
  template?: { id: string; code: string; name: string; nameAr: string | null } | null
  governorate?: string | null
  wilayat?: string | null
  latitude?: string | null
  longitude?: string | null
  placeCount?: number
  /** Assets in service or out of order; `issueCount` of them out of order. */
  assetCount?: number
  issueCount?: number
  archived: boolean
  version: number
}

/** A site as the sites register lists it, with its customer. */
export interface ServiceSiteRow extends ServiceSite {
  customer: ServiceCustomerRef
}

// ── items ────────────────────────────────────────────────────────────────────

const itemFields = z.object({
  /** A service or part from the catalogue; null for a free-text line. */
  variantId: nullableUuid,
  /** Defaults to the product's name; required for a free-text line. */
  description: optionalText(500),
  quantity: quantitySchema.refine((v) => Number(v) > 0, 'Quantity must be above zero'),
  unitPrice: moneySchema.refine((v) => Number(v) >= 0, 'Price cannot be negative'),
  /** Omitted: the product's tax rate (none for free text). null: no tax. */
  taxRateId: z.uuid().nullable().optional(),
  /** Omitted: chargeable on a chargeable job, covered on a contract job. */
  chargeable: z.boolean().optional(),
})
const describedOrProduct = (l: { variantId: string | null; description: string | null }) =>
  l.variantId !== null || Boolean(l.description)
const describeRule = { path: ['description'], message: 'Describe the item or choose one from the catalogue' }
export const serviceItemInputSchema = itemFields.refine(describedOrProduct, describeRule)
export type ServiceItemInput = z.input<typeof serviceItemInputSchema>
export const serviceItemUpdateSchema = itemFields
  .extend({ version: versionSchema })
  .refine(describedOrProduct, describeRule)
export type ServiceItemUpdate = z.input<typeof serviceItemUpdateSchema>
export const serviceItemRemoveSchema = z.object({ version: versionSchema })

export interface ServiceTaxRef {
  id: string
  name: string
  rate: string
}

/** How many usual items a job is offered. */
export const SERVICE_USUAL_ITEMS_MAX = 6

/**
 * `GET /service/jobs/:id/usual-items`: what this job's site, customer and company usually need, for a
 * one-tap add (the job's own items left out). The price is what this customer last paid for it, else
 * the catalogue price, else what was last charged for it.
 */
export interface ServiceUsualItem {
  variantId: string | null
  description: string
  unitPrice: string
  taxRate: ServiceTaxRef | null
  /** Where it comes from: this site's jobs, this customer's, or the company's frequent work. */
  source: 'SITE' | 'CUSTOMER' | 'COMPANY'
}

export interface ServiceJobItem {
  id: string
  lineNo: number
  variant: { id: string; sku: string | null; productType: 'GOODS' | 'SERVICE' } | null
  description: string
  quantity: string
  unitPrice: string
  taxRate: ServiceTaxRef | null
  /** quantity × unit price, before tax. */
  net: string
  tax: string
  total: string
  chargeable: boolean
  /** The accepted agreement (v1, v2…) covering its current values; null: needs a (new) agreement. */
  agreementSequence: number | null
  /** Agreed, or needing no agreement (covered by a contract, or approval is optional). */
  workable: boolean
  /** C2: on a job under a contract, why the item is covered or chargeable (null elsewhere). */
  coverageReason?: ContractCoverageReason | null
  completedQty: string
  /** On non-void invoices (drafts included). */
  invoicedQty: string
  /** Completed and not yet invoiced (chargeable items only). */
  invoiceableQty: string
  /** quantity − completed. */
  remainingQty: string
  version: number
}

// ── jobs ─────────────────────────────────────────────────────────────────────

const jobFields = z.object({
  /** The on-site contact; defaults to the location's contact. */
  contactName: optionalText(120),
  contactPhone: optionalText(40),
  issue: requiredText(2000, 'Describe the problem'),
  requestedService: optionalText(500),
  priority: servicePrioritySchema.default('NORMAL'),
  preferredDate: nullableDate,
  preferredDayPart: serviceDayPartSchema.nullish().transform((v) => v ?? null),
  /** The date the work is promised by; past it, an unfinished job is overdue. */
  targetDate: nullableDate,
  billingMode: serviceBillingModeSchema.default('CHARGEABLE'),
  /** The contract the job is covered by (CONTRACT jobs), as text: companies without the contracts module. */
  contractReference: optionalText(100),
  /** The team that owns the job (each visit names its own team). */
  teamId: nullableUuid,
  /** Who sold the job (ADR-049): its invoices credit them. Omitted = the customer's salesperson. */
  salespersonId: z.uuid().nullish(),
  notes: optionalText(2000),
  /** C1: where in the site (a place of the job's site), and the assets the job is about (of that site). */
  placeId: nullableUuid,
  assetIds: z.array(z.uuid()).max(200).default([]),
})

const contractOnlyWhenCovered = (v: { billingMode: ServiceBillingMode; contractId?: string | null }) =>
  !v.contractId || v.billingMode === 'CONTRACT'
const contractRule = { path: ['contractId'], message: 'Only a contract job names a contract' }

/**
 * `POST /service/jobs`: an existing location of the customer, or a new one recorded with the job.
 * C2: `contractId` (module contracts) must be ACTIVE today, the customer's, and cover the site
 * (CONTRACT_NOT_ACTIVE, CONTRACT_NOT_FOR_SITE); its terms set the items' covered/chargeable defaults.
 */
export const serviceJobCreateSchema = jobFields
  .extend({
    customerId: z.uuid(),
    siteId: nullableUuid,
    site: siteFields.nullish().transform((v) => v ?? null),
    items: z.array(serviceItemInputSchema).max(100).default([]),
    contractId: nullableUuid,
  })
  .refine((v) => (v.siteId === null) !== (v.site === null), {
    path: ['siteId'],
    message: 'Choose a service location or enter a new one',
  })
  .refine(contractOnlyWhenCovered, contractRule)
export type ServiceJobCreateInput = z.input<typeof serviceJobCreateSchema>

/** `PATCH /service/jobs/:id`: the intake details. The customer never changes. */
export const serviceJobUpdateSchema = jobFields
  .extend({
    siteId: z.uuid(),
    version: versionSchema,
    /** C1: omitted keeps the job's place and assets; null and [] clear them (a site change clears both anyway). */
    placeId: z.uuid().nullable().optional(),
    assetIds: z.array(z.uuid()).max(200).optional(),
    /** C2: omitted keeps the job's contract; null clears it. A planned job keeps its contract. */
    contractId: z.uuid().nullable().optional(),
  })
  .refine(contractOnlyWhenCovered, contractRule)
export type ServiceJobUpdateInput = z.input<typeof serviceJobUpdateSchema>

export const serviceJobCancelSchema = z.object({ version: versionSchema, reason: requiredText(1000) })
export const serviceJobWaitingSchema = z.object({
  version: versionSchema,
  waitingFor: serviceWaitingSchema.nullable(),
  note: optionalText(500),
})

/** Register views: each a list filter, its count shown on its tab. */
export const SERVICE_JOB_VIEWS = [
  'open',
  'awaiting',
  'scheduled',
  'unfinished',
  'ready',
  'completed',
  'cancelled',
  'all',
] as const
export type ServiceJobView = (typeof SERVICE_JOB_VIEWS)[number]
export const SERVICE_JOB_SORT_FIELDS = [
  'number',
  'createdAt',
  'priority',
  'targetDate',
  'customer',
  'nextVisit',
] as const

const flag = z.stringbool().optional()
const jobFilterFields = {
  status: serviceJobStatusSchema.optional(),
  billing: serviceBillingStatusSchema.optional(),
  payment: servicePaymentStatusSchema.optional(),
  priority: servicePrioritySchema.optional(),
  teamId: z.uuid().optional(),
  customerId: z.uuid().optional(),
  siteId: z.uuid().optional(),
  overdue: flag,
  waiting: serviceWaitingSchema.optional(),
  followUpPending: flag,
  collectionsAwaiting: flag,
  billingMode: serviceBillingModeSchema.optional(),
  /** C2: the jobs of one contract. */
  contractId: z.uuid().optional(),
  /** C3: planned jobs or requests. */
  origin: serviceJobOriginSchema.optional(),
}
export const serviceJobListQuerySchema = pageQuerySchema.extend({
  view: z.enum(SERVICE_JOB_VIEWS).default('open'),
  ...jobFilterFields,
})
export type ServiceJobListQuery = z.input<typeof serviceJobListQuerySchema>
export const serviceJobCountsQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  ...jobFilterFields,
})
export type ServiceJobCountsQuery = z.input<typeof serviceJobCountsQuerySchema>
export type ServiceJobCounts = Record<ServiceJobView, number>

export interface ServiceUserRef {
  id: string
  name: string
  nameAr: string | null
  active: boolean
}
export interface ServiceTeamRef {
  id: string
  name: string
  nameAr: string | null
}
export interface ServiceCustomerRef {
  id: string
  code: string
  name: string
  nameAr: string | null
}
export interface ServiceSiteRef {
  id: string
  name: string
  area: string | null
  address: string
}

/** A visit as lists, the board and the dashboard show it. */
export interface ServiceVisitSummary {
  id: string
  jobId: string
  jobNumber: string
  /** 1, 2, 3… within the job: shown as SJ-00012 · 2. */
  sequence: number
  kind: ServiceVisitKind
  status: ServiceVisitStatus
  outcome: ServiceVisitOutcome | null
  date: string
  startTime: string | null
  endTime: string | null
  durationMinutes: number | null
  team: ServiceTeamRef
  /** Null while the visit is booked for the team only (setting `visitLead: OPTIONAL`), until someone starts it. */
  lead: ServiceUserRef | null
  customer: ServiceCustomerRef
  site: ServiceSiteRef
  issue: string
  priority: ServicePriority
  rescheduleCount: number
  /** Scheduled and its date is before today. */
  missed: boolean
  /** C3: the job's origin, and how many places the visit covers (0 = the whole job). Optional for fixtures. */
  origin?: ServiceJobOrigin
  placeCount?: number
  /** C3: the places the visit covers (empty = the whole job), for "already in a visit" on the board. */
  placeIds?: string[]
  /** C3: a planned job's plan and period (null otherwise, or with the contracts module off). */
  plan?: ServiceJobPlan | null
  /**
   * Who to call and how to get there, for My visits' Call and Directions without opening the visit: the
   * job's on-site contact (else the site's) and the site's map link and point. Optional for fixtures.
   */
  reach?: {
    contactName: string | null
    contactPhone: string | null
    mapUrl: string | null
    latitude: string | null
    longitude: string | null
  }
}

/**
 * C3: the plan that made a planned job and the period it serves, so screens name it in the reader's
 * language ("Quarterly full service · Q3 2026") rather than the issue text the generator wrote.
 */
export interface ServiceJobPlan {
  name: string
  nameAr: string | null
  periodStart: string
  periodEnd: string
}

export interface ServiceJobListItem {
  id: string
  number: string
  customer: ServiceCustomerRef
  site: ServiceSiteRef
  issue: string
  priority: ServicePriority
  status: ServiceJobStatus
  billingMode: ServiceBillingMode
  billingStatus: ServiceBillingStatus
  paymentStatus: ServicePaymentStatus | null
  priceApproval: ServicePriceApproval
  team: ServiceTeamRef | null
  /** Who sold the job (ADR-049). Always sent; optional for older fixtures. */
  salesperson?: ServiceUserRef | null
  /** The next open visit (scheduled or under way), else null. */
  nextVisit: ServiceVisitSummary | null
  visitCount: number
  preferredDate: string | null
  /**
   * The part of the day the customer prefers (the board passes it to the scheduling dialog's hint).
   * Optional for older fixtures; the API always sends it.
   */
  preferredDayPart?: ServiceDayPart | null
  targetDate: string | null
  overdue: boolean
  waitingFor: ServiceWaiting | null
  followUpPending: boolean
  /** Collections reported and not yet verified. */
  collectionsAwaiting: number
  createdAt: string
  /** C3: planned by a contract, or a request. Optional for older fixtures (REQUEST). */
  origin?: ServiceJobOrigin
  /** C2: the contract's number (or the reference text without the module). */
  contractNumber?: string | null
  /** C1: the job's active assets (the board's "400 ACs"). */
  assetCount?: number
  /** C3: of the job's assets, how many are checked on any visit (null: checks off or no assets). */
  checks?: { checked: number; total: number } | null
  /** C3: planned jobs: the plan's minutes per asset × the assets (the board's "about 3 days of work"). */
  estimateMinutes?: number | null
  /** C3: a planned job's plan and period (null otherwise, or with the contracts module off). */
  plan?: ServiceJobPlan | null
}

/** C3: of the job's assets under one top-level place, how many there are and how many are checked. */
export interface ServicePlaceChecks {
  placeId: string
  total: number
  /** Checked on any visit of the job. */
  checked: number
}

export interface ServiceAgreementLine {
  itemId: string
  description: string
  quantity: string
  unitPrice: string
  taxRate: string | null
  total: string
}

export interface ServiceAgreement {
  id: string
  /** v1, v2… within the job. */
  sequence: number
  status: ServiceAgreementStatus
  method: ServiceAgreementMethod
  approverName: string | null
  approverPhone: string | null
  note: string | null
  total: string
  lines: ServiceAgreementLine[]
  visitId: string | null
  recordedBy: ServiceUserRef
  recordedAt: string
  decidedAt: string | null
  /** Optimistic version, for deciding an awaiting agreement. */
  version: number
}

export interface ServiceVisitReport {
  workPerformed: string | null
  findings: string | null
  condition: string | null
  unresolved: string | null
  recommendations: string | null
  nextAction: string | null
}

export interface ServiceFollowUp {
  id: string
  jobId: string
  jobNumber: string
  customer: ServiceCustomerRef
  site: ServiceSiteRef
  fromVisit: { id: string; sequence: number; date: string; outcome: ServiceVisitOutcome | null }
  team: ServiceTeamRef
  reason: ServiceFollowUpReason
  remainingWork: string
  proposedDate: string | null
  proposedStart: string | null
  proposedEnd: string | null
  preparation: string | null
  notes: string | null
  status: ServiceFollowUpStatus
  requestedBy: ServiceUserRef
  requestedAt: string
  decidedBy: ServiceUserRef | null
  decidedAt: string | null
  decisionNote: string | null
  /** The follow-up visit an approval scheduled. */
  visitId: string | null
  version: number
}

export interface ServiceJobInvoice {
  invoiceId: string
  number: string | null
  invoiceDate: string
  status: 'DRAFT' | 'POSTED' | 'VOID'
  total: string
  paid: string
  balance: string
  items: { itemId: string; quantity: string }[]
  visitId: string | null
  createdBy: ServiceUserRef
  createdAt: string
}

export interface ServiceCollection {
  id: string
  jobId: string
  jobNumber: string
  customer: ServiceCustomerRef
  invoice: { id: string; number: string | null; total: string; balance: string }
  amount: string
  method: PaymentMethod
  /** The company's own method (ADR-047), or null. Optional only for older fixtures. */
  paymentMethod?: PaymentMethodRef | null
  collectedOn: string
  collectedBy: ServiceUserRef
  reference: string | null
  notes: string | null
  status: ServiceCollectionStatus
  receipt: { id: string; number: string } | null
  verifiedBy: ServiceUserRef | null
  verifiedAt: string | null
  rejectReason: string | null
  visitId: string | null
  createdAt: string
  version: number
}

/**
 * Why invoicing and collecting are refused whatever the viewer may do: `SALES_OFF` while the Sales
 * module is off (invoices and receipts are sales documents). Null when nothing outside Service blocks it.
 */
export type ServiceInvoiceBlock = 'SALES_OFF'

export interface ServiceJobCapabilities {
  edit: boolean
  cancel: boolean
  setWaiting: boolean
  schedule: boolean
  editItems: boolean
  recordAgreement: boolean
  invoice: boolean
  collect: boolean
  verifyCollections: boolean
  decideFollowUps: boolean
}

export interface ServiceJobTotals {
  /** Every active chargeable item at its current values, tax included. */
  scope: string
  /** Of the scope, what the latest accepted agreement covers. */
  agreed: string
  /** Completed chargeable work at its price, tax included. */
  completed: string
  /** Posted invoices made from the job. */
  invoiced: string
  paid: string
  balance: string
  /** Collections reported and not yet verified. */
  reported: string
}

export interface ServiceJobDetail extends ServiceJobListItem {
  contactName: string | null
  contactPhone: string | null
  requestedService: string | null
  preferredDayPart: ServiceDayPart | null
  contractReference: string | null
  notes: string | null
  waitingNote: string | null
  cancelReason: string | null
  cancelledAt: string | null
  completedAt: string | null
  createdBy: ServiceUserRef
  siteDetail: ServiceSite
  /** C1: the place in the site and the assets the job is about (optional for older fixtures). */
  place?: { id: string; path: string; steps: PlaceStep[] } | null
  assets?: AssetRef[]
  /**
   * C3: the assets' check counts per top-level place, in the order of `assets` (assets on the site itself
   * are left out); null while checklists are off. Optional for older fixtures.
   */
  placeChecks?: ServicePlaceChecks[] | null
  /** C2: the contract the job is under; null without one, or with the contracts module off. */
  contractId?: string | null
  contract?: JobContract | null
  items: ServiceJobItem[]
  agreements: ServiceAgreement[]
  visits: ServiceVisitSummary[]
  followUps: ServiceFollowUp[]
  invoices: ServiceJobInvoice[]
  collections: ServiceCollection[]
  totals: ServiceJobTotals
  capabilities: ServiceJobCapabilities
  /** Set when invoicing, collecting and verifying are off for the company (the caps are then false). */
  invoiceBlockedBy: ServiceInvoiceBlock | null
  /** Simple mode: the job sits under the company's approval threshold, so no price approval is asked for. */
  priceApprovalWaived?: boolean
  version: number
}

// ── price approvals ──────────────────────────────────────────────────────────

const decisionFields = {
  method: serviceAgreementMethodSchema,
  approverName: optionalText(120),
  approverPhone: optionalText(40),
  note: optionalText(1000),
}
const namedWhenDecided = (v: { decision: string; approverName: string | null }) =>
  v.decision === 'AWAITING' || Boolean(v.approverName)
const namedRule = { path: ['approverName'], message: 'Who agreed (or declined) for the customer?' }

/**
 * `POST /service/jobs/:id/agreements`: snapshots the job's active items as the next version. AWAITING:
 * the price was sent and the customer will answer later. `expectedTotal` is the total the customer was
 * shown; if the scope changed since, the answer is 409 SCOPE_CHANGED.
 */
export const serviceAgreementInputSchema = z
  .object({
    decision: z.enum(['ACCEPTED', 'DECLINED', 'AWAITING']),
    ...decisionFields,
    visitId: nullableUuid,
    expectedTotal: moneySchema,
  })
  .refine(namedWhenDecided, namedRule)
export type ServiceAgreementInput = z.input<typeof serviceAgreementInputSchema>

/** `POST /service/agreements/:id/decide`: the customer's answer to an awaiting agreement. */
export const serviceAgreementDecideSchema = z
  .object({ decision: z.enum(['ACCEPTED', 'DECLINED']), ...decisionFields, version: versionSchema })
  .refine(namedWhenDecided, namedRule)
export type ServiceAgreementDecideInput = z.input<typeof serviceAgreementDecideSchema>

// ── visits ───────────────────────────────────────────────────────────────────

const slotFields = z.object({
  teamId: z.uuid(),
  /** Null books the team without a lead: allowed only with the setting `visitLead: OPTIONAL`. */
  leadId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  date: z.iso.date(),
  startTime: nullableTime,
  endTime: nullableTime,
  durationMinutes: z
    .number()
    .int()
    .min(15)
    .max(1440)
    .nullish()
    .transform((v) => v ?? null),
  /** Schedule even though the window overlaps another visit of the team or the lead. */
  confirmOverlap: z.boolean().default(false),
})
/**
 * C3: the places of the job's site the visit covers (each with its subtree); empty = the whole job. Splits a
 * planned job over several visits ("floors 1–10 on Monday"). PLACE_NOT_AT_SITE when one isn't the site's.
 */
const visitPlacesSchema = z
  .array(z.uuid())
  .max(200)
  .refine((l) => new Set(l).size === l.length, 'Each place once')
export const serviceVisitScheduleSchema = slotFields
  .extend({ notes: optionalText(1000), placeIds: visitPlacesSchema.default([]) })
  .refine(windowOk, windowRule)
export type ServiceVisitScheduleInput = z.input<typeof serviceVisitScheduleSchema>

/**
 * Before attendance only; the old values stay in the history. `placeIds` omitted keeps them. A move to
 * another day or team is a reschedule: it needs the reason and counts. A change of window or lead on the
 * same day with the same team is a small adjustment: no reason, not counted (the API decides which).
 */
export const serviceVisitRescheduleSchema = slotFields
  .extend({ reason: optionalText(500), version: versionSchema, placeIds: visitPlacesSchema.optional() })
  .refine(windowOk, windowRule)
export type ServiceVisitRescheduleInput = z.input<typeof serviceVisitRescheduleSchema>

/** A scheduled visit is cancelled with an optional reason (the chips fill one; "Other" may say more). */
export const serviceVisitCancelSchema = z.object({ version: versionSchema, reason: optionalText(500) })
export const serviceVisitStepSchema = z.object({ version: versionSchema })

const reportFields = {
  workPerformed: optionalText(4000),
  findings: optionalText(4000),
  condition: optionalText(2000),
  unresolved: optionalText(2000),
  recommendations: optionalText(2000),
  nextAction: optionalText(1000),
}

/** `PATCH /service/visits/:id/report`: saved as the team goes, until the visit finishes. */
export const serviceVisitReportSchema = z.object({ version: versionSchema, ...reportFields })
export type ServiceVisitReportInput = z.input<typeof serviceVisitReportSchema>

/** `POST /service/visits/:id/work`: the quantity of each item done on this visit (replaces the visit's). */
export const serviceVisitWorkSchema = z.object({
  version: versionSchema,
  work: z
    .array(
      z.object({
        itemId: z.uuid(),
        quantity: quantitySchema.refine((v) => Number(v) >= 0, 'Quantity cannot be negative'),
      }),
    )
    .max(100),
})
export type ServiceVisitWorkInput = z.input<typeof serviceVisitWorkSchema>

/**
 * `POST /service/visits/:id/finish`. FULL records every remaining workable quantity as done on this
 * visit; NONE needs a reason. The report fields given here are saved with it.
 */
export const serviceVisitFinishSchema = z
  .object({
    version: versionSchema,
    outcome: serviceVisitOutcomeSchema,
    outcomeNote: optionalText(1000),
    acknowledgedBy: optionalText(120),
    ...reportFields,
  })
  .refine((v) => v.outcome !== 'NONE' || Boolean(v.outcomeNote), {
    path: ['outcomeNote'],
    message: 'Say why no work was done',
  })
export type ServiceVisitFinishInput = z.input<typeof serviceVisitFinishSchema>

/** The longest period one visit list covers (My visits looks back 90 days for missed visits). */
export const SERVICE_VISIT_LIST_MAX_DAYS = 92
/** The most visits one list answers; a range holding more is refused (narrow it), never cut. */
export const SERVICE_VISIT_LIST_MAX = 500
export const serviceVisitListQuerySchema = z.object({
  /** Default today; `to` defaults to `from`. At most SERVICE_VISIT_LIST_MAX_DAYS days. */
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  teamId: z.uuid().optional(),
  /** Visits of the teams the caller belongs to. */
  mine: flag,
  status: serviceVisitStatusSchema.optional(),
  /** Scheduled or under way. */
  open: flag,
})
export type ServiceVisitListQuery = z.input<typeof serviceVisitListQuerySchema>

export interface ServiceVisitCapabilities {
  reschedule: boolean
  cancel: boolean
  start: boolean
  beginWork: boolean
  editItems: boolean
  recordAgreement: boolean
  recordWork: boolean
  editReport: boolean
  finish: boolean
  requestFollowUp: boolean
  invoice: boolean
  collect: boolean
  /** C3: the viewer may record per-asset checks now (switch on, visit under way, their team). */
  recordChecks?: boolean
  /** The viewer may add photos and files to the visit: its team or a service manager, until cancelled. */
  attach?: boolean
  /**
   * The viewer may remove the visit's files: its team while the visit is open (not finished or
   * cancelled), a service manager on a finished visit too.
   */
  removeFiles?: boolean
}

/**
 * What the visit a follow-up visit follows left for it: the approved request that booked it (null when
 * the office booked the visit directly) and that visit's own words on what stayed undone.
 */
export interface ServiceVisitLeftOver {
  request: {
    id: string
    reason: ServiceFollowUpReason
    remainingWork: string
    /** The service manager's note on approving. */
    decisionNote: string | null
  } | null
  visit: {
    id: string
    sequence: number
    date: string
    outcome: ServiceVisitOutcome | null
    /** Its report's "unresolved issues". */
    unresolved: string | null
    /** Why no work was done, when none was. */
    outcomeNote: string | null
  }
}

export interface ServiceVisitDetail extends ServiceVisitSummary {
  notes: string | null
  arrivedAt: string | null
  startedAt: string | null
  finishedAt: string | null
  outcomeNote: string | null
  acknowledgedBy: string | null
  report: ServiceVisitReport
  cancelReason: string | null
  followsVisit: { id: string; sequence: number; date: string } | null
  /** On a follow-up visit, what the visit it follows left (null otherwise; optional for older fixtures). */
  leftFromLast?: ServiceVisitLeftOver | null
  /** Quantity done on this visit, per item. */
  work: { itemId: string; quantity: string }[]
  /** The follow-up requested from this visit, if any. */
  followUp: ServiceFollowUp | null
  contactName: string | null
  contactPhone: string | null
  siteDetail: ServiceSite
  /** C1: the job's place and assets, as on the job (optional for older fixtures; empty with the register off). */
  place?: { id: string; path: string; steps: PlaceStep[] } | null
  assets?: AssetRef[]
  /** The job's scope as the technician sees it (items with done / remaining). */
  items: ServiceJobItem[]
  billingMode: ServiceBillingMode
  billingStatus: ServiceBillingStatus
  priceApproval: ServicePriceApproval
  /** Simple mode: the job sits under the company's approval threshold, so no price approval is asked for. */
  priceApprovalWaived?: boolean
  latestAgreement: ServiceAgreement | null
  invoices: ServiceJobInvoice[]
  collections: ServiceCollection[]
  photoCount: number
  capabilities: ServiceVisitCapabilities
  /** Set when invoicing and collecting are off for the company (the caps are then false). */
  invoiceBlockedBy: ServiceInvoiceBlock | null
  /** C2: the job's contract (as on the job). */
  contract?: JobContract | null
  /** C3: the places the visit covers (empty: the whole job). */
  scope?: {
    id: string
    path: string
    steps: PlaceStep[]
    /** The site template's word for the place's level ("Floor"), null without a template. */
    level?: { en: string; ar: string } | null
  }[]
  /** C3: the per-asset checklist (null without assets on the job). */
  checklist?: VisitChecklist | null
  version: number
}

/** `GET /service/availability`: the team's and the lead's visits that day, for the scheduling dialog. */
export const serviceAvailabilityQuerySchema = z.object({
  date: z.iso.date(),
  teamId: z.uuid(),
  leadId: z.uuid().optional(),
  /** The visit being rescheduled (left out). */
  visitId: z.uuid().optional(),
})
export interface ServiceAvailability {
  date: string
  team: ServiceVisitSummary[]
  lead: ServiceVisitSummary[]
}

// ── follow-ups ───────────────────────────────────────────────────────────────

const followUpFields = z.object({
  reason: serviceFollowUpReasonSchema,
  remainingWork: requiredText(2000, 'Say what is left to do'),
  proposedDate: nullableDate,
  proposedStart: nullableTime,
  proposedEnd: nullableTime,
  preparation: optionalText(1000),
  notes: optionalText(1000),
})
const proposalOk = (v: { proposedStart: string | null; proposedEnd: string | null }) =>
  v.proposedStart === null || v.proposedEnd === null || v.proposedEnd > v.proposedStart
const proposalRule = { path: ['proposedEnd'], message: 'The end is before the start' }
export const serviceFollowUpInputSchema = followUpFields.refine(proposalOk, proposalRule)
export type ServiceFollowUpInput = z.input<typeof serviceFollowUpInputSchema>
export const serviceFollowUpResubmitSchema = followUpFields
  .extend({ version: versionSchema })
  .refine(proposalOk, proposalRule)
export type ServiceFollowUpResubmitInput = z.input<typeof serviceFollowUpResubmitSchema>

/** Approving schedules the follow-up visit (the proposal, adjusted as the manager decides). */
export const serviceFollowUpApproveSchema = slotFields
  .extend({ version: versionSchema, note: optionalText(1000) })
  .refine(windowOk, windowRule)
export type ServiceFollowUpApproveInput = z.input<typeof serviceFollowUpApproveSchema>
/** `POST /service/follow-ups/:id/approve-as-proposed`: the manager's one tap (ADR-050 shortcut). */
export const serviceFollowUpApproveAsProposedSchema = z.object({ version: versionSchema })
export const serviceFollowUpReturnSchema = z.object({ version: versionSchema, note: requiredText(1000) })
export const serviceFollowUpWithdrawSchema = z.object({ version: versionSchema })
export const serviceFollowUpListQuerySchema = pageQuerySchema.extend({
  status: serviceFollowUpStatusSchema.default('PENDING'),
})
export type ServiceFollowUpListQuery = z.input<typeof serviceFollowUpListQuerySchema>

// ── invoicing ────────────────────────────────────────────────────────────────

/**
 * `POST /service/jobs/:id/invoices`: one posted sales invoice for the given completed quantities. The
 * same `requestId` again returns the same invoice (retry, double tap).
 */
export const serviceInvoiceInputSchema = z.object({
  requestId: requestIdSchema,
  visitId: nullableUuid,
  invoiceDate: z.iso.date().optional(),
  items: z
    .array(
      z.object({
        itemId: z.uuid(),
        quantity: quantitySchema.refine((v) => Number(v) > 0, 'Quantity must be above zero'),
      }),
    )
    .min(1, 'Choose at least one item')
    .max(100),
})
export type ServiceInvoiceInput = z.input<typeof serviceInvoiceInputSchema>
export interface ServiceInvoiceResult {
  invoiceId: string
  number: string | null
  total: string
  /** False when the request id had already been used: the first invoice is returned. */
  created: boolean
}

// ── collections ──────────────────────────────────────────────────────────────

export const serviceCollectionInputSchema = z.object({
  requestId: requestIdSchema,
  invoiceId: z.uuid(),
  amount: moneySchema.refine((v) => Number(v) > 0, 'Enter an amount above zero'),
  method: paymentMethodSchema,
  /** The company's own method (its type must be `method`); the receipt made from it keeps it. */
  paymentMethodId: paymentMethodIdSchema,
  collectedOn: z.iso.date(),
  /** The employee who received the money; defaults to the caller. */
  collectedById: nullableUuid,
  reference: optionalText(100),
  notes: optionalText(500),
  visitId: nullableUuid,
})
export type ServiceCollectionInput = z.input<typeof serviceCollectionInputSchema>

export const serviceCollectionVerifySchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('CREATE_RECEIPT'),
    version: versionSchema,
    treasuryAccountId: z.uuid(),
    receiptDate: z.iso.date(),
    /**
     * Required for a collection by cheque, refused with any other method (ADR-046): the receipt registers
     * the cheque IN_HAND and holds the money under collection until the bank pays it.
     */
    cheque: receivedChequeInputSchema.nullish().transform((v) => v ?? null),
  }),
  z.object({ mode: z.literal('LINK_RECEIPT'), version: versionSchema, receiptId: z.uuid() }),
])
export type ServiceCollectionVerifyInput = z.input<typeof serviceCollectionVerifySchema>
export const serviceCollectionRejectSchema = z.object({ version: versionSchema, reason: requiredText(500) })
export const serviceCollectionListQuerySchema = pageQuerySchema.extend({
  status: serviceCollectionStatusSchema.default('REPORTED'),
  jobId: z.uuid().optional(),
})
export type ServiceCollectionListQuery = z.input<typeof serviceCollectionListQuerySchema>

// ── teams, board, dashboard ──────────────────────────────────────────────────

export interface ServiceTeam extends ServiceTeamRef {
  members: { user: ServiceUserRef; role: 'LEAD' | 'MEMBER' }[]
}

/** The longest period one board request covers. */
export const SERVICE_BOARD_MAX_DAYS = 14
export const serviceBoardQuerySchema = z.object({
  from: z.iso.date(),
  to: z.iso.date(),
  teamId: z.uuid().optional(),
})
export type ServiceBoardQuery = z.input<typeof serviceBoardQuerySchema>
export interface ServiceBoard {
  from: string
  to: string
  today: string
  teams: (ServiceTeam & { visits: ServiceVisitSummary[] })[]
  /** Open REQUEST jobs with no open visit and no pending follow-up, most urgent first. */
  unscheduled: ServiceJobListItem[]
  unscheduledTotal: number
  /**
   * C3: planned jobs not fully scheduled (no open visit, or assets outside every open visit's places), by
   * target date. The panel groups them by contract and site. Optional for older fixtures.
   */
  planned?: ServiceJobListItem[]
  plannedTotal?: number
  /** Follow-up requests awaiting a decision. */
  followUps: ServiceFollowUp[]
}

export interface ServiceDashboard {
  today: string
  awaitingAssignment: number
  visitsToday: number
  pendingFollowUps: number
  unfinished: number
  readyToInvoice: number
  collectionsAwaiting: number
  /** Their total: null unless the caller collects or verifies payments (or manages service). */
  collectionsAwaitingAmount: string | null
  overdue: number
  /** C3: planned jobs waiting to be scheduled (optional for older fixtures). */
  plannedAwaiting?: number
  /** Set when completed work cannot be invoiced nor collections verified (the counts stay accurate). */
  invoiceBlockedBy: ServiceInvoiceBlock | null
  /** Today's visits, earliest first (the dashboard groups them by team). */
  todaysVisits: ServiceVisitSummary[]
}

/**
 * `GET /service/my-day`: the service part of My work. Today's visits of the caller's teams and the
 * visits they lead (not cancelled, earliest first), and the service decisions waiting for them. A count
 * is null when the caller may not take that decision.
 */
export interface ServiceMyDay {
  date: string
  visits: ServiceVisitSummary[]
  decisions: {
    /** Pending follow-up requests (holders of service.approve). */
    followUpsToDecide: number | null
    /** Reported collections and their total (holders of service.verify_payments). */
    collectionsToVerify: { count: number; amount: string } | null
    /** The caller's follow-up requests a manager returned with a note, latest first. */
    returnedFollowUps: ServiceFollowUp[]
  }
}
